/**
 * SuggestedMoveCard — Push-Mode "One-Tap Resolve" hero.
 * ─────────────────────────────────────────────────────
 * Reframes the resolve modal around a single recommended next move
 * derived from the issue's scenario. The operator approves rather
 * than describes — that's the streamline standard.
 *
 * Three buttons:
 *   • "Do this"          → executes the suggested move (caller wires onDoThis)
 *   • "Adjust"           → opens the inline editor below (caller wires onAdjust)
 *   • "Different action" → reveals the manual form (caller wires onManual)
 *
 * The card is intentionally dumb — it surfaces a label + reason + buttons.
 * All execution lives in the parent so existing audit / scheduling /
 * resolution paths are untouched. Non-destructive.
 */
import * as React from "react";
import {
  Sparkles,
  Calendar,
  Phone,
  Package,
  HardHat,
  Wrench,
  FileCheck,
  CheckCircle2,
  Pencil,
  ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { IssueScenario } from "@/data/situational/requiredInputs";

export type SuggestedMoveIcon =
  | "schedule"
  | "call"
  | "order"
  | "crew"
  | "service"
  | "permit"
  | "generic";

export interface SuggestedMove {
  /** Visual cue (left side icon). */
  icon: SuggestedMoveIcon;
  /** Short, action-led headline. e.g. "Reschedule install with Alpha Crew". */
  title: string;
  /** Plain-language reason / context. e.g. "Customer requested Tue, Apr 21 9 AM". */
  reason: string;
  /** Label for the primary one-tap button. Defaults to "Do this". */
  doLabel?: string;
  /** When set, "Adjust" button is rendered. */
  canAdjust?: boolean;
  /** When false, "Different action" is hidden (rare scenarios). */
  canManual?: boolean;
}

export interface SuggestedMoveCardProps {
  move: SuggestedMove | null;
  /** Mode controls visual weight — "hero" is the big top-of-drawer slot. */
  variant?: "hero" | "compact";
  /** Tap "Do this" → caller decides what executes. */
  onDoThis: () => void;
  /** Tap "Adjust" → caller opens inline editor. */
  onAdjust?: () => void;
  /** Tap "Different action" → caller reveals manual form. */
  onManual?: () => void;
  className?: string;
}

const ICON_MAP: Record<SuggestedMoveIcon, React.ComponentType<{ className?: string }>> = {
  schedule: Calendar,
  call: Phone,
  order: Package,
  crew: HardHat,
  service: Wrench,
  permit: FileCheck,
  generic: ListChecks,
};

export function SuggestedMoveCard({
  move,
  variant = "hero",
  onDoThis,
  onAdjust,
  onManual,
  className,
}: SuggestedMoveCardProps) {
  if (!move) return null;
  const Icon = ICON_MAP[move.icon] ?? ListChecks;
  const isHero = variant === "hero";

  return (
    <div
      className={cn(
        "rounded-lg border border-primary/30 bg-gradient-to-br from-primary/[0.06] via-primary/[0.03] to-transparent",
        isHero ? "p-3.5" : "p-2.5",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "rounded-md bg-primary/15 text-primary shrink-0 flex items-center justify-center",
            isHero ? "h-9 w-9" : "h-7 w-7",
          )}
          aria-hidden
        >
          <Icon className={isHero ? "h-4.5 w-4.5" : "h-3.5 w-3.5"} />
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-primary" aria-hidden />
            <span className="text-[10px] uppercase tracking-wider font-bold text-primary">
              Suggested move
            </span>
          </div>
          <p className={cn("font-semibold text-foreground", isHero ? "text-sm" : "text-xs")}>
            {move.title}
          </p>
          <p className="text-[11px] text-muted-foreground leading-snug">{move.reason}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-3 pl-12">
        <Button
          size="sm"
          onClick={onDoThis}
          className="h-8 gap-1.5 bg-primary hover:bg-primary/90"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {move.doLabel ?? "Do this"}
        </Button>
        {move.canAdjust && onAdjust && (
          <Button
            size="sm"
            variant="outline"
            onClick={onAdjust}
            className="h-8 gap-1.5"
          >
            <Pencil className="h-3.5 w-3.5" />
            Adjust
          </Button>
        )}
        {(move.canManual ?? true) && onManual && (
          <button
            type="button"
            onClick={onManual}
            className="ml-auto text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            Different action
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Scenario → Suggested Move mapper (Resolve drawer specialization)
// ────────────────────────────────────────────────────────────────────
//
// Pure helper. Given the resolved scenario + a few opt-in context bits,
// returns the single best move to show in the hero. Falls back to null
// for "generic" so callers can render the manual form directly.
//
// Kept here (not in src/data/) because this mapping is presentation —
// it phrases situational truth as a one-tap CTA. Business rules live
// in availableActions.ts and requiredInputs.ts.

export interface ResolveMoveContext {
  scenario: IssueScenario;
  /** True when an outreach action (call/email) is the actual resolution path. */
  isOutreachOnly: boolean;
  /** Optional vendor name for material scenarios — sharpens the headline. */
  topVendorName?: string;
  /** Optional crew/contractor name for scheduling/contractor scenarios. */
  topContractorName?: string;
  /** Optional customer first name for personalization. */
  customerFirstName?: string;
}

export function getResolveSuggestedMove(ctx: ResolveMoveContext): SuggestedMove | null {
  const customer = ctx.customerFirstName ? ctx.customerFirstName : "the homeowner";

  if (ctx.isOutreachOnly) {
    return {
      icon: "call",
      title: `Call ${customer} to resolve this`,
      reason: "Status, contact log, and target date update automatically from the call dispo.",
      doLabel: "Open call dispo",
      canAdjust: false,
      canManual: true,
    };
  }

  switch (ctx.scenario) {
    case "scheduling":
      return {
        icon: "schedule",
        title: ctx.topContractorName
          ? `Book a visit with ${ctx.topContractorName}`
          : "Book a visit at the next available slot",
        reason:
          "We'll log the booking, set the new target date, and mark the issue In Progress in one tap.",
        doLabel: "Book visit",
        canAdjust: true,
        canManual: true,
      };
    case "material":
      return {
        icon: "order",
        title: ctx.topVendorName
          ? `Reorder from ${ctx.topVendorName}`
          : "Reorder the missing material",
        reason: "Generates a PO draft, pings the vendor, and notifies the customer.",
        doLabel: "Generate PO",
        canAdjust: true,
        canManual: true,
      };
    case "access":
      return {
        icon: "call",
        title: `Reach ${customer} to confirm site access`,
        reason: "Outreach is auto-logged. Status moves to In Progress on contact attempt.",
        doLabel: "Log outreach",
        canAdjust: false,
        canManual: true,
      };
    case "service":
      return {
        icon: "service",
        title: "Schedule a service visit",
        reason: "Books the repair, notifies the homeowner, and routes the right crew.",
        doLabel: "Schedule service",
        canAdjust: true,
        canManual: true,
      };
    case "permit":
      return {
        icon: "permit",
        title: "Log municipality follow-up",
        reason: "Captures the contact, advances permit stage, and sets a follow-up date.",
        doLabel: "Log follow-up",
        canAdjust: true,
        canManual: true,
      };
    default:
      // Generic scenario → no hero, manual form is the right surface.
      return null;
  }
}
